import type { Booking } from '@/types';
import type {
  DemandPlanGroupBookingPatch,
  DemandPlanGroupDeleteRequest,
  DemandPlanGroupUpdateRequest,
} from '@/services/api';

/** Value written to Booking.source by the open-demand allocation endpoints. */
export const DEMAND_SOURCE = 'Demand';

/**
 * True for bookings created by accepting a plan on the Open Demand screen.
 * Deliberately independent of recurrence: a demand booking is never part of a
 * repeat series, and a repeat occurrence is never part of a demand plan group.
 */
export function isDemandBooking(
  booking: Pick<Booking, 'source' | 'demandPlanGroupId'>
): boolean {
  return (
    !!booking.demandPlanGroupId ||
    String(booking.source ?? '').toLowerCase() === DEMAND_SOURCE.toLowerCase()
  );
}

/**
 * True when the booking belongs to a group of linked demand bookings, i.e. the
 * bulk delete/reassign/edit actions can act on its siblings.
 */
export function isLinkedDemandBooking(
  booking: Pick<Booking, 'source' | 'demandPlanGroupId'>
): boolean {
  return !!booking.demandPlanGroupId;
}

export type DemandReassignScope = 'this' | 'all';

export function normalizeDemandReassignScope(value: unknown): DemandReassignScope | undefined {
  return value === 'this' || value === 'all' ? value : undefined;
}

export function shouldPromptDemandReassign(
  booking: Pick<Booking, 'source' | 'demandPlanGroupId'>,
  resourceChanged: boolean,
  scope: unknown
): boolean {
  return (
    isLinkedDemandBooking(booking)
    && resourceChanged
    && normalizeDemandReassignScope(scope) === undefined
  );
}

export function buildDemandPlanGroupDeleteRequest(): DemandPlanGroupDeleteRequest {
  return {};
}

const demandPlanGroupPatchFields = [
  ['projectName', 'title'],
  ['description', 'description'],
  ['bookingTypeId', 'bookingTypeId'],
  ['projectId', 'projectId'],
  ['clientId', 'clientId'],
  ['costCentreId', 'costCentreId'],
  ['billingClassification', 'billingClassification'],
  ['workType', 'workType'],
  ['status', 'status'],
  ['priority', 'priority'],
  ['color', 'color'],
  ['customer', 'customer'],
  ['location', 'location'],
  ['details', 'details'],
  ['taskDescription', 'taskDescription'],
  ['bookingNotes', 'bookingNotes'],
  ['bookingReference', 'bookingReference'],
  ['costCentreTask', 'costCentreTask'],
  ['requiredSkills', 'requiredSkills'],
  ['requiredRole', 'requiredRole'],
  ['durationType', 'durationType'],
  ['halfDayPeriod', 'halfDayPeriod'],
  ['durationHours', 'durationHours'],
  ['userChar', 'userChar'],
  ['userNumber', 'userNumber'],
  ['userFlag', 'userFlag'],
  ['userDate', 'userDate'],
  ['userNotes', 'userNotes'],
  ['userTime', 'userTime'],
] as const;

type DemandPlanGroupPatchField = (typeof demandPlanGroupPatchFields)[number][1];

function toDemandPlanGroupPatchValue(
  field: DemandPlanGroupPatchField,
  value: unknown
): unknown {
  if (value == null) return value;
  if (
    field === 'bookingTypeId'
    || field === 'projectId'
    || field === 'clientId'
    || field === 'costCentreId'
    || field === 'durationHours'
  ) {
    return Number(value);
  }
  if (
    field === 'requiredSkills'
    || field === 'userChar'
    || field === 'userNumber'
    || field === 'userFlag'
    || field === 'userDate'
    || field === 'userNotes'
    || field === 'userTime'
  ) {
    return typeof value === 'string' ? value : JSON.stringify(value);
  }
  return value;
}

function dateValue(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? undefined : parsed;
}

function valuesMatch(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * Builds the server-side demand-group update contract from the modal's partial
 * booking update. Temporal fields are intentionally sent only when the edited
 * booking's start or end actually changed; the backend propagates them to the
 * rest of the group. The edited booking's original start is sent as the cutoff so
 * the change — and the booking-history entry written per booking — covers the
 * edited booking and its future linked bookings, never earlier ones.
 */
export function buildDemandPlanGroupUpdatePayload(
  bookingId: string | number,
  originalBooking: Booking,
  changes: Partial<Booking>,
  updatedBy?: string
): DemandPlanGroupUpdateRequest {
  const patchValues: Record<string, unknown> = {};
  const changeValues = changes as Record<string, unknown>;
  const originalValues = originalBooking as unknown as Record<string, unknown>;
  for (const [sourceField, targetField] of demandPlanGroupPatchFields) {
    if (
      sourceField in changeValues
      && changeValues[sourceField] !== undefined
      && !valuesMatch(changeValues[sourceField], originalValues[sourceField])
    ) {
      patchValues[targetField] = toDemandPlanGroupPatchValue(targetField, changeValues[sourceField]);
    }
  }

  const originalStart = originalBooking.startTime;
  const originalEnd = originalBooking.endTime;
  const newStart = changes.startTime ?? originalStart;
  const newEnd = changes.endTime ?? originalEnd;
  const temporalChanged =
    dateValue(newStart) !== dateValue(originalStart)
    || dateValue(newEnd) !== dateValue(originalEnd);

  const payload: DemandPlanGroupUpdateRequest = {
    bookingId: Number(bookingId),
    fromStartTime: originalBooking.startTime,
    changes: patchValues as DemandPlanGroupBookingPatch,
  };
  if (updatedBy) payload.updatedBy = updatedBy;
  if (temporalChanged) {
    payload.originalStartTime = originalStart;
    payload.originalEndTime = originalEnd;
    payload.newStartTime = newStart;
    payload.newEndTime = newEnd;
  }
  return payload;
}

export interface DemandBookingTemporalPatch {
  id: string | number;
  startTime: string;
  endTime: string;
  durationHours?: number;
}

function formatLocalDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
}

function calendarDayNumber(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000;
}

/**
 * Propagates an accepted demand plan's anchor time change across its bookings.
 * The date moves by the anchor's calendar-day delta. Time-of-day and duration
 * are propagated only when the anchor changed those values.
 */
export function propagateDemandBookingTemporalUpdate(
  originalAnchor: Pick<Booking, 'startTime' | 'endTime'>,
  updatedAnchor: Pick<Booking, 'startTime' | 'endTime'>,
  bookings: ReadonlyArray<Pick<Booking, 'id' | 'startTime' | 'endTime'>>
): DemandBookingTemporalPatch[] {
  const originalStart = new Date(originalAnchor.startTime);
  const originalEnd = new Date(originalAnchor.endTime);
  const updatedStart = new Date(updatedAnchor.startTime);
  const updatedEnd = new Date(updatedAnchor.endTime);
  const dayDelta = calendarDayNumber(updatedStart) - calendarDayNumber(originalStart);
  const timeChanged = updatedStart.getHours() !== originalStart.getHours()
    || updatedStart.getMinutes() !== originalStart.getMinutes()
    || updatedStart.getSeconds() !== originalStart.getSeconds()
    || updatedStart.getMilliseconds() !== originalStart.getMilliseconds();
  const originalDuration = originalEnd.getTime() - originalStart.getTime();
  const updatedDuration = updatedEnd.getTime() - updatedStart.getTime();
  const durationChanged = updatedDuration !== originalDuration;

  return bookings.map(booking => {
    const oldStart = new Date(booking.startTime);
    const oldEnd = new Date(booking.endTime);
    const shiftedStart = new Date(oldStart);
    shiftedStart.setDate(shiftedStart.getDate() + dayDelta);
    if (timeChanged) {
      shiftedStart.setHours(
        updatedStart.getHours(),
        updatedStart.getMinutes(),
        updatedStart.getSeconds(),
        updatedStart.getMilliseconds(),
      );
    }
    const nextEnd = new Date(
      shiftedStart.getTime() + (durationChanged ? updatedDuration : oldEnd.getTime() - oldStart.getTime()),
    );
    return {
      id: booking.id,
      startTime: formatLocalDateTime(shiftedStart),
      endTime: formatLocalDateTime(nextEnd),
      ...(durationChanged ? { durationHours: updatedDuration / 3_600_000 } : {}),
    };
  });
}
